import { orderBy, toNumber, uniqBy } from 'lodash-es';
import type { LocalMessage } from '../conversation';
import {
  useSetQAPairMeta,
  useQAPairConversationKnowledgeQuery,
  getRangeTimestamp,
} from './useQAPair';
import { VALID_PERIOD } from './useSenderQAPairMsgMark';
import { isIdEqual } from 'utils/is';
import { useSetPaneHeight } from '../conversationsHooks/useConversationPane';
import { useCallback } from 'react';
import { useSetSelectedAnswerMsgIds } from './useMessageSelect';

const getValidKnowledgeQueries = (allQueries: Conversation.Message[]) => {
  return allQueries.filter((item) => {
    const itemSendTime = toNumber(item.sendTime);
    return Date.now() - itemSendTime <= VALID_PERIOD;
  });
};

export const getQAQueries = (knowledgeReplies?: Conversation.TopBarInfo['knowledgeReplies']) => {
  const knowledgeQueryValues = knowledgeReplies
    ? Object.values(knowledgeReplies).map((knowledgeReply) => knowledgeReply.query)
    : [];
  const allQueries = orderBy(uniqBy(knowledgeQueryValues, 'msgId'), 'sendTime', 'asc');
  const validQueries = getValidKnowledgeQueries(allQueries);
  return {
    allQueries,
    validQueries,
  };
};

export const useQAPairTriggerFromBuyer = () => {
  const setQAPairMeta = useSetQAPairMeta();
  const setPaneHeight = useSetPaneHeight();

  const conversationKnowledgeQuery = useQAPairConversationKnowledgeQuery();

  return useCallback(
    (
      conversation: Conversation.Conversation,
      message: LocalMessage,
      source: SupplementKnowledge.Source,
    ) => {
      if (!conversationKnowledgeQuery) {
        return;
      }
      const {
        conversationId: conversationKnowledgeQueryConversationId,
        knowledgeQuery,
        knowledgeReply,
      } = conversationKnowledgeQuery;
      const { conversationId } = conversation;
      if (!isIdEqual(conversationKnowledgeQueryConversationId, conversationId)) {
        return;
      }

      const { sendTime } = message;
      const messageSendTime = toNumber(sendTime);
      const rangeTimestamp =
        source === 'CUSTOMER' ? messageSendTime : getRangeTimestamp(message.sendTime);
      const selectMode = source === 'CUSTOMER' ? 'single' : 'multiple';

      let defaultKnowledgeQueryMessage = message;
      if (
        source === 'CUSTOMER' &&
        knowledgeQuery &&
        isIdEqual(knowledgeQuery.msgId, message.msgId)
      ) {
        defaultKnowledgeQueryMessage = knowledgeQuery;
      }
      if (
        source === 'AI_ERROR_REPLY' &&
        knowledgeReply &&
        isIdEqual(knowledgeReply.msgId, message.msgId)
      ) {
        defaultKnowledgeQueryMessage = knowledgeReply;
      }

      setQAPairMeta({
        visible: true,
        source,
        selectMode,
        rangeTimestamp,
        defaultKnowledgeQueryMessage,
      });
      setPaneHeight(120);
    },
    [conversationKnowledgeQuery, setPaneHeight, setQAPairMeta],
  );
};

export const useQAPairTriggerFromAssistant = () => {
  const setQAPairMeta = useSetQAPairMeta();
  const setPaneHeight = useSetPaneHeight();

  const conversationKnowledgeQuery = useQAPairConversationKnowledgeQuery();
  const setSelectedAnswerMsgIds = useSetSelectedAnswerMsgIds('', true);

  const trigger = useCallback(
    (
      conversation: Conversation.Conversation,
      message: LocalMessage,
      source: SupplementKnowledge.Source,
    ) => {
      if (!conversationKnowledgeQuery) {
        return;
      }
      const { conversationId: conversationKnowledgeQueryConversationId } =
        conversationKnowledgeQuery;
      const { conversationId } = conversation;
      if (!isIdEqual(conversationKnowledgeQueryConversationId, conversationId)) {
        return;
      }

      const { msgId, sendTime } = message;
      const messageSendTime = toNumber(sendTime);

      if (source === 'CUSTOMER') {
        const { knowledgeQueries } = conversationKnowledgeQuery;
        const lastQuery = knowledgeQueries.findLast((item) => {
          const itemSendTime = toNumber(item.sendTime);
          return itemSendTime <= messageSendTime;
        });
        if (lastQuery) {
          const rangeTimestamp = toNumber(lastQuery.sendTime);
          setQAPairMeta({
            visible: true,
            source: 'CUSTOMER',
            selectMode: 'single',
            rangeTimestamp,
            defaultKnowledgeQueryMessage: lastQuery,
          });
          setSelectedAnswerMsgIds(new Set([msgId]));
          setPaneHeight(120);
        }
      }

      if (source === 'AI_ERROR_REPLY') {
        const { knowledgeReplies } = conversationKnowledgeQuery;
        const replyQueries = Object.values(knowledgeReplies).map((item) => item.query);
        const lastQuery = replyQueries.findLast((item) => {
          const itemSendTime = toNumber(item.sendTime);
          return itemSendTime <= messageSendTime;
        });
        if (lastQuery) {
          const rangeTimestamp = getRangeTimestamp(lastQuery.sendTime);
          setQAPairMeta({
            visible: true,
            source: 'AI_ERROR_REPLY',
            selectMode: 'multiple',
            rangeTimestamp,
            defaultKnowledgeQueryMessage: lastQuery,
          });
          setSelectedAnswerMsgIds(new Set([msgId]));
          setPaneHeight(120);
        }
      }
    },
    [conversationKnowledgeQuery, setPaneHeight, setQAPairMeta, setSelectedAnswerMsgIds],
  );

  return trigger;
};
